<script>
  import { onMount, createEventDispatcher } from 'svelte';
  import { selectedOrgId, isCrossTenantOrgContext } from '../../lib/orgContext.js';
  import { selectedRunId } from '../../lib/store.js';
  import { formatSignedPercentValue } from '../../lib/strategyResult.js';
  import api from '../../lib/api.js';
  import SymbolUniverse from '../shared/SymbolUniverse.svelte';

  const dispatch = createEventDispatcher();

  let runs = [];
  let state = 'loading'; // 'loading' | 'ready' | 'error'

  export async function reload() {
    // TASK-20260818: no-org skip (a scope-less tenant read is a guaranteed 400 during the startup
    // window — the org-change reactive below reloads once the org lands) + capture-then-compare so
    // a slow response for a superseded org never renders that org's runs.
    const orgAtTrigger = $selectedOrgId;
    if (!orgAtTrigger) return;
    state = 'loading';
    try {
      const data = await api.getBacktestRuns(orgAtTrigger);
      if ($selectedOrgId !== orgAtTrigger) return;
      runs = Array.isArray(data) ? data : [];
      state = 'ready';
    } catch (err) {
      if ($selectedOrgId !== orgAtTrigger) return;
      console.error('Failed to load run history:', err);
      state = 'error';
    }
  }

  onMount(reload);

  // Reload when the org changes (history is org-scoped).
  let lastOrg = $selectedOrgId;
  $: if ($selectedOrgId !== lastOrg) {
    lastOrg = $selectedOrgId;
    reload();
  }

  function statusClass(status) {
    if (status === 'done') return 'badge-success';
    if (status === 'failed') return 'badge-danger';
    return 'badge-warning'; // running
  }

  function select(run) {
    // Pass the whole summary (TASK-20260728) so the results header + skeletons can render instantly from its
    // stored fields while the heavy getBacktestRun read is in flight.
    dispatch('select', { runId: run.runId, summary: run });
  }

  function fmtDate(d) {
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString();
  }

  // Inline delete: a trash icon flips the row into a confirm state; only "Delete" actually removes it.
  // `confirmingId` holds the run id currently asking for confirmation (one at a time); `deletingId` guards
  // the in-flight request so the buttons disable and a double-click can't fire two deletes.
  let confirmingId = null;
  let deletingId = null;

  function askDelete(runId) {
    confirmingId = runId;
  }

  function cancelDelete() {
    confirmingId = null;
  }

  async function confirmDelete(run) {
    if (deletingId) return;
    deletingId = run.runId;
    try {
      await api.deleteBacktestRun(run.runId, $selectedOrgId);
      runs = runs.filter((r) => r.runId !== run.runId);
      // If the deleted run was the one open in the results pane, tell the parent to clear it.
      if ($selectedRunId === run.runId) {
        dispatch('deleted', { runId: run.runId });
      }
    } catch (err) {
      console.error('Failed to delete run:', err);
      dispatch('deleteError', { runId: run.runId });
    } finally {
      deletingId = null;
      confirmingId = null;
    }
  }
</script>

<div class="run-history">
  <div class="run-history-header">
    <h3 class="run-history-title">Run History</h3>
    <button class="btn btn-ghost btn-sm" on:click={reload} disabled={state === 'loading'}>Refresh</button>
  </div>

  {#if state === 'loading'}
    <div class="run-history-skeleton">
      {#each Array(4) as _, i (i)}
        <div class="skeleton run-history-skeleton-row"></div>
      {/each}
    </div>
  {:else if state === 'error'}
    <div class="alert alert-danger">
      Couldn't load run history.
      <button class="btn btn-ghost btn-sm" on:click={reload}>Retry</button>
    </div>
  {:else if runs.length === 0}
    <div class="empty-state">
      <h4 class="empty-state-title">No runs yet</h4>
      <p class="empty-state-description">Configure your first backtest above — it'll appear here.</p>
    </div>
  {:else}
    <ul class="run-history-list">
      {#each runs as run (run.runId)}
        <li class="run-history-row">
          <button
            class="run-history-item"
            class:selected={$selectedRunId === run.runId}
            on:click={() => select(run)}
          >
            <div class="run-history-item-top">
              <span class="run-history-strategy">{run.strategy}</span>
              <span class="badge {statusClass(run.status)}">{run.status}</span>
            </div>
            <div class="run-history-item-mid">
              <span class="run-history-symbols"><SymbolUniverse symbols={run.symbols} dense expandable={false} label="Pairs" /></span>
              <span class="run-history-date">{fmtDate(run.createdAt)}</span>
            </div>
            <div class="run-history-item-metrics">
              <span>Net {formatSignedPercentValue(run.netReturns)}</span>
              <span>Sharpe {run.sharpeRatio === null || run.sharpeRatio === undefined ? '—' : Number(run.sharpeRatio).toFixed(2)}</span>
            </div>
          </button>

          <!-- Another org's runs are read-only (TASK-20260830 AC 4) — the server 403s the delete,
               so the affordance goes away rather than offering a refusal. -->
          {#if $isCrossTenantOrgContext}
            <!-- no delete affordance in an assigned org's context -->
          {:else if confirmingId === run.runId}
            <div class="run-history-confirm" role="group" aria-label="Confirm delete">
              <span class="run-history-confirm-text">Delete this run?</span>
              <button
                class="btn btn-danger btn-sm"
                on:click|stopPropagation={() => confirmDelete(run)}
                disabled={deletingId === run.runId}
              >
                {deletingId === run.runId ? 'Deleting…' : 'Delete'}
              </button>
              <button
                class="btn btn-ghost btn-sm"
                on:click|stopPropagation={cancelDelete}
                disabled={deletingId === run.runId}
              >
                Cancel
              </button>
            </div>
          {:else}
            <button
              class="run-history-delete"
              title="Delete run"
              aria-label="Delete run {run.strategy} from {fmtDate(run.createdAt)}"
              on:click|stopPropagation={() => askDelete(run.runId)}
            >
              <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">
                <path fill="currentColor" d="M6.5 1a1 1 0 0 0-1 1v.5H3a.75.75 0 0 0 0 1.5h.3l.6 9.06A1.75 1.75 0 0 0 5.64 15h4.72a1.75 1.75 0 0 0 1.74-1.44L12.7 4H13a.75.75 0 0 0 0-1.5h-2.5V2a1 1 0 0 0-1-1h-3Zm.5 1.5h2V2.5h-2v0Zm-.4 4a.75.75 0 0 1 .75.75v4a.75.75 0 0 1-1.5 0v-4A.75.75 0 0 1 6.6 6.5Zm3.55.75a.75.75 0 0 0-1.5 0v4a.75.75 0 0 0 1.5 0v-4Z"/>
              </svg>
            </button>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}
</div>

<style>
  @import './RunHistory.css';
</style>
